import React, { useState, useEffect, useCallback, useMemo } from 'react';
import masterService from '../../../services/masterService';
import MasterHeader from '../components/MasterHeader';
import ConfirmModal from '../components/ConfirmModal';

export const PanelWiseRateTab = ({ showToast, initialPanelId = null }) => {
  // Panels list for dropdown
  const [panels, setPanels] = useState([]);
  const [panelsLoading, setPanelsLoading] = useState(false);
  const [selectedPanelId, setSelectedPanelId] = useState(initialPanelId || 1);

  // Departments for filtering
  const [departments, setDepartments] = useState([]);

  // Bifurcation Item Type Filter: 'ALL' | 'TEST' | 'PROFILE' | 'PACKAGE'
  const [itemTypeFilter, setItemTypeFilter] = useState('ALL');

  // Rates data
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');

  // Modified rows: { [itemId]: { itemId, rate: number, mrpRate: number, itemDisplayName: string, itemCode: string } }
  const [modifiedRows, setModifiedRows] = useState({});

  // Selection for bulk actions
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  // Bulk rate inputs
  const [bulkRateInput, setBulkRateInput] = useState('');
  const [bulkDiscountInput, setBulkDiscountInput] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('fixed'); // 'fixed' | 'percent'
  const [saving, setSaving] = useState(false);

  // 1. Fetch Panels and Departments on mount
  useEffect(() => {
    const loadLookups = async () => {
      setPanelsLoading(true);
      try {
        const [panelRes, deptRes] = await Promise.all([
          masterService.getPanels({ isActive: true }),
          masterService.getDepartments({ isActive: true })
        ]);

        if (panelRes.success && panelRes.data) {
          setPanels(panelRes.data);
          if (!selectedPanelId && panelRes.data.length > 0) {
            setSelectedPanelId(panelRes.data[0].panelId);
          }
        }
        if (deptRes.success && deptRes.data) {
          setDepartments(deptRes.data);
        }
      } catch (err) {
        showToast?.('Failed to load panels and departments.', 'error');
      } finally {
        setPanelsLoading(false);
      }
    };

    loadLookups();
  }, [showToast, selectedPanelId]);

  // 2. Fetch Rates for Selected Panel (supports itemType)
  const fetchPanelRates = useCallback(async () => {
    if (!selectedPanelId) return;
    setLoading(true);
    try {
      const res = await masterService.getPanelRates(selectedPanelId, {
        subCategoryId: selectedDept === 'all' ? undefined : Number(selectedDept),
        search: search.trim() || undefined,
        itemType: itemTypeFilter !== 'ALL' ? itemTypeFilter : undefined
      });
      if (res.success && res.data) {
        setRates(res.data);
        setModifiedRows({});
        setSelectedItemIds(new Set());
      }
    } catch (err) {
      showToast?.('Failed to load panel rate tariff schedule.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedPanelId, selectedDept, search, itemTypeFilter, showToast]);

  useEffect(() => {
    fetchPanelRates();
  }, [fetchPanelRates]);

  // Current selected panel metadata
  const currentPanel = useMemo(() => {
    return panels.find(p => p.panelId === Number(selectedPanelId)) || null;
  }, [panels, selectedPanelId]);

  // Category counts from current rates or for tabs
  const typeCounts = useMemo(() => {
    let testCount = 0;
    let profileCount = 0;
    let packageCount = 0;

    rates.forEach(r => {
      const type = (r.itemType || 'TEST').toUpperCase();
      if (type === 'PACKAGE') packageCount++;
      else if (type === 'PROFILE') profileCount++;
      else testCount++;
    });

    return {
      all: rates.length,
      test: testCount,
      profile: profileCount,
      package: packageCount
    };
  }, [rates]);

  // Handle single item rate change
  const handleRateChange = (item, newRateVal) => {
    const val = parseFloat(newRateVal);
    if (isNaN(val) || val < 0) return;

    setModifiedRows(prev => ({
      ...prev,
      [item.itemId]: {
        itemId: item.itemId,
        rate: val,
        mrpRate: item.mrpRate,
        itemDisplayName: item.itemDisplayName || item.investigationName,
        itemCode: item.testCode
      }
    }));
  };

  // Toggle selection for bulk actions
  const toggleSelectItem = (itemId) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItemIds.size === rates.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(rates.map(r => r.itemId)));
    }
  };

  // Bulk Apply Handler
  const handleApplyBulk = () => {
    if (selectedItemIds.size === 0) return;

    if (bulkActionType === 'fixed') {
      const rate = parseFloat(bulkRateInput);
      if (isNaN(rate) || rate < 0) {
        showToast?.('Please enter a valid non-negative rate.', 'error');
        return;
      }
      setModifiedRows(prev => {
        const next = { ...prev };
        rates.forEach(r => {
          if (selectedItemIds.has(r.itemId)) {
            next[r.itemId] = {
              itemId: r.itemId,
              rate: rate,
              mrpRate: r.mrpRate,
              itemDisplayName: r.itemDisplayName || r.investigationName,
              itemCode: r.testCode
            };
          }
        });
        return next;
      });
      showToast?.(`Applied ₹${rate} to ${selectedItemIds.size} items. Click 'Save Rates' to persist.`, 'info');
    } else if (bulkActionType === 'percent') {
      const pct = parseFloat(bulkDiscountInput);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        showToast?.('Please enter a discount percentage between 0 and 100.', 'error');
        return;
      }
      setModifiedRows(prev => {
        const next = { ...prev };
        rates.forEach(r => {
          if (selectedItemIds.has(r.itemId)) {
            const calculatedRate = Math.round(r.mrpRate * (1 - pct / 100));
            next[r.itemId] = {
              itemId: r.itemId,
              rate: Math.max(0, calculatedRate),
              mrpRate: r.mrpRate,
              itemDisplayName: r.itemDisplayName || r.investigationName,
              itemCode: r.testCode
            };
          }
        });
        return next;
      });
      showToast?.(`Applied ${pct}% discount to ${selectedItemIds.size} items. Click 'Save Rates' to persist.`, 'info');
    }
    setShowBulkConfirm(false);
  };

  // Save All Dirty Changes
  const handleSaveAll = async () => {
    const dirtyItems = Object.values(modifiedRows);
    if (dirtyItems.length === 0) {
      showToast?.('No changes to save.', 'info');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        panelId: Number(selectedPanelId),
        items: dirtyItems
      };
      const res = await masterService.batchUpdatePanelRates(selectedPanelId, payload);
      if (res.success) {
        showToast?.(`Successfully updated ${dirtyItems.length} tariff rates for panel!`, 'success');
        await fetchPanelRates();
      } else {
        showToast?.(res.message || 'Failed to save rates.', 'error');
      }
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Error saving panel rates.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(modifiedRows).length;

  return (
    <div className="mst-tab-content">
      {/* Top Header */}
      <MasterHeader
        title="Panel-Wise Rate List"
        subtitle="Configure and bulk-update tariffs for Tests, Clinical Profiles, and Health Packages for a specific Cash Billing Panel."
        icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        }
        actions={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {dirtyCount > 0 && (
              <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#f59e0b', backgroundColor: '#fef3c7', padding: '6px 12px', borderRadius: 20 }}>
                ● {dirtyCount} Unsaved Changes
              </span>
            )}
            <button
              type="button"
              className="mst-btn-secondary"
              onClick={fetchPanelRates}
              disabled={loading || saving}
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              className="mst-btn-primary"
              onClick={handleSaveAll}
              disabled={dirtyCount === 0 || saving}
              style={{ minWidth: 140 }}
            >
              {saving ? 'Saving...' : `Save Rates (${dirtyCount})`}
            </button>
          </div>
        }
      />

      {/* Control Panel Bar */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        
        {/* Item Type Bifurcation Segmented Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginRight: 6 }}>
              Tariff Item View:
            </span>
            <button
              type="button"
              onClick={() => {
                if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching views will discard them. Continue?')) return;
                setItemTypeFilter('ALL');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: '0.86rem',
                fontWeight: itemTypeFilter === 'ALL' ? 700 : 500,
                border: itemTypeFilter === 'ALL' ? '1.5px solid #0f172a' : '1px solid #cbd5e1',
                backgroundColor: itemTypeFilter === 'ALL' ? '#f8fafc' : '#ffffff',
                color: itemTypeFilter === 'ALL' ? '#0f172a' : '#475569',
                cursor: 'pointer'
              }}
            >
              🔬 All Items
              {itemTypeFilter === 'ALL' && (
                <span style={{ fontSize: '0.72rem', backgroundColor: '#e2e8f0', color: '#1e293b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                  {typeCounts.all}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching views will discard them. Continue?')) return;
                setItemTypeFilter('TEST');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: '0.86rem',
                fontWeight: itemTypeFilter === 'TEST' ? 700 : 500,
                border: itemTypeFilter === 'TEST' ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                backgroundColor: itemTypeFilter === 'TEST' ? '#f0f9ff' : '#ffffff',
                color: itemTypeFilter === 'TEST' ? '#0369a1' : '#475569',
                cursor: 'pointer'
              }}
            >
              🧪 Single Tests
              <span style={{ fontSize: '0.72rem', backgroundColor: itemTypeFilter === 'TEST' ? '#bae6fd' : '#f1f5f9', color: itemTypeFilter === 'TEST' ? '#0369a1' : '#64748b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                {itemTypeFilter === 'ALL' ? typeCounts.test : typeCounts.all}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching views will discard them. Continue?')) return;
                setItemTypeFilter('PROFILE');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: '0.86rem',
                fontWeight: itemTypeFilter === 'PROFILE' ? 700 : 500,
                border: itemTypeFilter === 'PROFILE' ? '1.5px solid #9333ea' : '1px solid #cbd5e1',
                backgroundColor: itemTypeFilter === 'PROFILE' ? '#faf5ff' : '#ffffff',
                color: itemTypeFilter === 'PROFILE' ? '#7e22ce' : '#475569',
                cursor: 'pointer'
              }}
            >
              📋 Clinical Profiles
              <span style={{ fontSize: '0.72rem', backgroundColor: itemTypeFilter === 'PROFILE' ? '#e9d5ff' : '#f1f5f9', color: itemTypeFilter === 'PROFILE' ? '#7e22ce' : '#64748b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                {itemTypeFilter === 'ALL' ? typeCounts.profile : typeCounts.all}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching views will discard them. Continue?')) return;
                setItemTypeFilter('PACKAGE');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: '0.86rem',
                fontWeight: itemTypeFilter === 'PACKAGE' ? 700 : 500,
                border: itemTypeFilter === 'PACKAGE' ? '1.5px solid #059669' : '1px solid #cbd5e1',
                backgroundColor: itemTypeFilter === 'PACKAGE' ? '#ecfdf5' : '#ffffff',
                color: itemTypeFilter === 'PACKAGE' ? '#047857' : '#475569',
                cursor: 'pointer'
              }}
            >
              📦 Health Packages
              <span style={{ fontSize: '0.72rem', backgroundColor: itemTypeFilter === 'PACKAGE' ? '#a7f3d0' : '#f1f5f9', color: itemTypeFilter === 'PACKAGE' ? '#047857' : '#64748b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                {itemTypeFilter === 'ALL' ? typeCounts.package : typeCounts.all}
              </span>
            </button>
          </div>

          <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
            Panel: <strong style={{ color: '#0f172a' }}>{currentPanel?.panelName || 'Walk-In'}</strong> ({currentPanel?.panelCode})
          </div>
        </div>

        {/* Filters Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr auto', gap: 16, alignItems: 'center' }}>
          {/* Panel Selector */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
              Select Cash Billing Panel
            </label>
            <select
              className="mst-form-select"
              value={selectedPanelId}
              onChange={(e) => {
                if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching panels will discard them. Continue?')) {
                  return;
                }
                setSelectedPanelId(Number(e.target.value));
              }}
              disabled={panelsLoading || loading}
              style={{ fontWeight: 600, fontSize: '0.95rem' }}
            >
              {panels.map(p => (
                <option key={p.panelId} value={p.panelId}>
                  {p.panelName} ({p.panelCode}) — {p.centreName}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
              Search Item / Code
            </label>
            <input
              type="text"
              className="mst-form-input"
              placeholder="Search by item name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Department Filter */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
              Department
            </label>
            <select
              className="mst-form-select"
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
            >
              <option value="all">All Departments ({departments.length})</option>
              {departments.map(d => (
                <option key={d.subCategoryId} value={d.subCategoryId}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Panel Context Info Badge */}
          <div style={{ padding: '8px 14px', backgroundColor: '#f1f5f9', borderRadius: 8, textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Rate Mode</div>
            <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a' }}>
              {currentPanel?.referenceCodeOPD && currentPanel.referenceCodeOPD !== currentPanel.panelId ? (
                <span style={{ color: '#0284c7' }}>Inherits from #{currentPanel.referenceCodeOPD}</span>
              ) : (
                <span style={{ color: '#059669' }}>Independent Tariff</span>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Action Strip (Visible when items selected) */}
        {selectedItemIds.size > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8fafc', padding: 12, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
                ✓ {selectedItemIds.size} items selected
              </span>
              <button
                type="button"
                className="mst-btn-secondary"
                style={{ fontSize: '0.78rem', padding: '4px 8px' }}
                onClick={() => setSelectedItemIds(new Set())}
              >
                Clear Selection
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Fixed Rate Input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Set Fixed Rate: ₹</span>
                <input
                  type="number"
                  placeholder="300"
                  style={{ width: 90, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                  value={bulkRateInput}
                  onChange={(e) => setBulkRateInput(e.target.value)}
                />
                <button
                  type="button"
                  className="mst-btn-primary"
                  style={{ fontSize: '0.82rem', padding: '5px 12px' }}
                  onClick={() => {
                    const rate = parseFloat(bulkRateInput);
                    if (isNaN(rate) || rate < 0) {
                      showToast?.('Please enter a valid non-negative rate.', 'error');
                      return;
                    }
                    setModifiedRows(prev => {
                      const next = { ...prev };
                      rates.forEach(r => {
                        if (selectedItemIds.has(r.itemId)) {
                          next[r.itemId] = {
                            itemId: r.itemId,
                            rate: rate,
                            mrpRate: r.mrpRate,
                            itemDisplayName: r.itemDisplayName || r.investigationName,
                            itemCode: r.testCode
                          };
                        }
                      });
                      return next;
                    });
                    showToast?.(`Applied ₹${rate} to ${selectedItemIds.size} items. Click 'Save Rates' to persist.`, 'success');
                  }}
                  disabled={!bulkRateInput}
                >
                  Apply Rate
                </button>
              </div>

              <span style={{ color: '#cbd5e1' }}>|</span>

              {/* Discount % Input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Discount:</span>
                <input
                  type="number"
                  placeholder="20"
                  style={{ width: 70, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                  value={bulkDiscountInput}
                  onChange={(e) => setBulkDiscountInput(e.target.value)}
                />
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>% off MRP</span>
                <button
                  type="button"
                  className="mst-btn-primary"
                  style={{ fontSize: '0.82rem', padding: '5px 12px' }}
                  onClick={() => {
                    const pct = parseFloat(bulkDiscountInput);
                    if (isNaN(pct) || pct < 0 || pct > 100) {
                      showToast?.('Please enter a discount percentage between 0 and 100.', 'error');
                      return;
                    }
                    setModifiedRows(prev => {
                      const next = { ...prev };
                      rates.forEach(r => {
                        if (selectedItemIds.has(r.itemId)) {
                          const calculatedRate = Math.round(r.mrpRate * (1 - pct / 100));
                          next[r.itemId] = {
                            itemId: r.itemId,
                            rate: Math.max(0, calculatedRate),
                            mrpRate: r.mrpRate,
                            itemDisplayName: r.itemDisplayName || r.investigationName,
                            itemCode: r.testCode
                          };
                        }
                      });
                      return next;
                    });
                    showToast?.(`Applied ${pct}% discount to ${selectedItemIds.size} items. Click 'Save Rates' to persist.`, 'success');
                  }}
                  disabled={!bulkDiscountInput}
                >
                  Apply %
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rates Grid */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        <table className="mst-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ width: 40, padding: '10px 14px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={rates.length > 0 && selectedItemIds.size === rates.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th style={{ width: 45, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>#</th>
              <th style={{ width: 90, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>Category</th>
              <th style={{ width: 110, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Item Code</th>
              <th style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Item / Tariff Name</th>
              <th style={{ width: 160, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Department</th>
              <th style={{ width: 120, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Standard MRP</th>
              <th style={{ width: 120, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Current Rate</th>
              <th style={{ width: 150, padding: '10px 14px', fontSize: '0.78rem', color: '#0f172a', textTransform: 'uppercase', textAlign: 'right' }}>New Rate (₹)</th>
              <th style={{ width: 85, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                  <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid #cbd5e1', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <div style={{ marginTop: 8, fontSize: '0.88rem' }}>Loading panel rate schedules...</div>
                </td>
              </tr>
            ) : rates.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                  No items found matching your filter criteria.
                </td>
              </tr>
            ) : (
              rates.map((item, idx) => {
                const isSelected = selectedItemIds.has(item.itemId);
                const isModified = modifiedRows[item.itemId] !== undefined;
                const activeRate = isModified ? modifiedRows[item.itemId].rate : item.panelRate;
                const type = (item.itemType || 'TEST').toUpperCase();

                return (
                  <tr
                    key={item.itemId}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: isModified ? '#fffbeb' : isSelected ? '#f0f9ff' : idx % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}
                  >
                    <td style={{ textAlign: 'center', padding: '8px 14px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectItem(item.itemId)}
                      />
                    </td>
                    <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.82rem' }}>{idx + 1}</td>
                    
                    {/* Item Type Badge */}
                    <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        padding: '3px 8px',
                        borderRadius: 6,
                        backgroundColor: type === 'PACKAGE' ? '#ecfdf5' : type === 'PROFILE' ? '#faf5ff' : '#f0f9ff',
                        color: type === 'PACKAGE' ? '#047857' : type === 'PROFILE' ? '#7e22ce' : '#0369a1',
                        border: type === 'PACKAGE' ? '1px solid #a7f3d0' : type === 'PROFILE' ? '1px solid #e9d5ff' : '1px solid #bae6fd',
                        display: 'inline-block'
                      }}>
                        {type}
                      </span>
                    </td>

                    <td style={{ padding: '8px 14px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: '0.8rem' }}>
                        {item.testCode || 'N/A'}
                      </span>
                    </td>

                    <td style={{ padding: '8px 14px', fontWeight: 600, color: '#1e293b' }}>
                      <div>{item.investigationName}</div>
                      {type === 'PACKAGE' && (
                        <div style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 500, marginTop: 2 }}>
                          📦 Health Package • {item.activeComponentCount || 0} Components included
                        </div>
                      )}
                      {type === 'PROFILE' && (
                        <div style={{ fontSize: '0.74rem', color: '#7e22ce', fontWeight: 500, marginTop: 2 }}>
                          📋 Clinical Profile • {item.activeComponentCount || 0} Tests included
                        </div>
                      )}
                      {item.itemDisplayName && item.itemDisplayName !== item.investigationName && (
                        <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Alt: {item.itemDisplayName}</div>
                      )}
                    </td>

                    <td style={{ padding: '8px 14px', fontSize: '0.84rem', color: '#475569' }}>
                      {item.departmentName}
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: '0.88rem', color: '#64748b' }}>
                      ₹{item.mrpRate.toFixed(2)}
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                      ₹{item.panelRate.toFixed(2)}
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>₹</span>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          style={{
                            width: 95,
                            padding: '6px 8px',
                            textAlign: 'right',
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            borderRadius: 6,
                            border: isModified ? '2px solid #f59e0b' : '1px solid #cbd5e1',
                            backgroundColor: isModified ? '#ffffff' : '#f8fafc',
                            color: isModified ? '#b45309' : '#0f172a'
                          }}
                          value={activeRate}
                          onChange={(e) => handleRateChange(item, e.target.value)}
                        />
                      </div>
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                      {isModified ? (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, backgroundColor: '#fef3c7', color: '#d97706' }}>
                          DIRTY
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, backgroundColor: '#dcfce7', color: '#16a34a' }}>
                          SAVED
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Safety Bulk Confirmation Modal */}
      <ConfirmModal
        isOpen={showBulkConfirm}
        title="Confirm Bulk Rate Update"
        message={
          bulkActionType === 'fixed'
            ? `You are about to apply a fixed rate of ₹${bulkRateInput} to ${selectedItemIds.size} selected items for panel '${currentPanel?.panelName}'. These changes will remain in dirty state until you click 'Save Rates'.`
            : `You are about to apply a ${bulkDiscountInput}% discount off MRP across ${selectedItemIds.size} selected items for panel '${currentPanel?.panelName}'.`
        }
        confirmLabel="Apply to Selected"
        cancelLabel="Cancel"
        onConfirm={handleApplyBulk}
        onCancel={() => setShowBulkConfirm(false)}
      />
    </div>
  );
};

export default PanelWiseRateTab;
